import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Text } from 'ui-components/components/text';

import messages from './messages';
import style from './style.module.less';
import { PromotionalBanner } from '../../../../../../components/promotional-banner';
import { useGlobalModalLaunchContext } from '../../../../../../contexts/modal-context/global-modal-context';
import { getLink } from '../../../../../../services/link-service';
import { linkIDs } from '../../../../../../services/link-service/links';
import Global from '../../../../../../store/global-selectors';
import { useReturnsTabContext } from '../hooks/use-returns-tab-context';

const PROMOTE_ENHANCED_RETURNS = 'promoteEnhancedReturns';
const PROMOTE_STANDARD_RETURNS = 'promoteStandardReturns';

const getBannerTypeIfAny = (
  storeSupportsEnhancedReturns,
  isLegacyBrandedReturnsFeatureAvailableOnAnyPlan,
  hasLegacyBrandedReturns,
  isEnhancedReturnsAvailableOnAnyPlan,
  isEnhancedReturnsFeatureEnabled,
) => {
  if (!storeSupportsEnhancedReturns && hasLegacyBrandedReturns) {
    return null;
  }

  if (storeSupportsEnhancedReturns && isEnhancedReturnsFeatureEnabled) {
    return null;
  }

  if (!storeSupportsEnhancedReturns && !hasLegacyBrandedReturns && isLegacyBrandedReturnsFeatureAvailableOnAnyPlan) {
    return PROMOTE_STANDARD_RETURNS;
  }

  if (storeSupportsEnhancedReturns && !isEnhancedReturnsFeatureEnabled && isEnhancedReturnsAvailableOnAnyPlan) {
    return PROMOTE_ENHANCED_RETURNS;
  }
};

/**
 * Returns promotional banner component
 * Displays a promotional banner for the appropriate returns feature when applicable
 * @returns {React.ReactElement|null} The returns promotional banner or null if it shouldn't be displayed
 */
export const ReturnsPromotionalBanner = () => {
  const {
    hasLegacyBrandedReturns,
    isLegacyBrandedReturnsFeatureAvailableOnAnyPlan,
    isEnhancedReturnsAvailableOnAnyPlan,
    isEnhancedReturnsFeatureEnabled,
    isShipstationReturnsEnabled,
    isStoreSetupCompleteForEnhancedReturns,
    marketplaceName,
    supportsEnhancedReturns,
  } = useReturnsTabContext();

  const intl = useIntl();
  const supportCountry = useSelector((state) => Global.selectSellerSupportCountry(state));
  const helpArticleHref = getLink(linkIDs.EnhancedReturns, supportCountry, intl.locale);
  const { openManageSubscriptionDrawer } = useGlobalModalLaunchContext();

  if (isShipstationReturnsEnabled && isStoreSetupCompleteForEnhancedReturns) return null;

  const bannerType = getBannerTypeIfAny(
    supportsEnhancedReturns,
    isLegacyBrandedReturnsFeatureAvailableOnAnyPlan,
    hasLegacyBrandedReturns,
    isEnhancedReturnsAvailableOnAnyPlan,
    isEnhancedReturnsFeatureEnabled,
  );
  const promotionalHeaderMessage =
    bannerType === PROMOTE_ENHANCED_RETURNS ? (
      <FormattedMessage {...messages.promotionalBannerEnhancedReturnsTitle} values={{ marketplaceName }} />
    ) : (
      <FormattedMessage {...messages.promotionalBannerStandardReturnsTitle} />
    );

  if (bannerType == null) return null;

  return (
    <PromotionalBanner
      className={style.activateReturnsBanner}
      promotionalHeaderMessage={promotionalHeaderMessage}
      promotionalSubheaderMessage={messages.promotionalBannerText}
    >
      <Button onClick={openManageSubscriptionDrawer} theme='promotional'>
        <FormattedMessage {...messages.upgradePlan} />
      </Button>
      <Button href={helpArticleHref} theme='link'>
        <Text value={messages.learnMore} />
      </Button>
    </PromotionalBanner>
  );
};
